import type { QueryKey } from '@tanstack/react-query';

import { ENDPOINTS as IssuesV2Endpoints } from '../../../../shared/issuesV2';
import useQuery from '../../../ReactQuery/useQuery';
import { get } from '../../../redux/api';
import type { IssueTypesSummary, Scope } from '../../../../shared/issuesV2';

/**
 * Fetches the issue types summary for a given scope.
 * Returns counts of issues by type.
 */
function fetchIssueTypesSummary(scope: Scope) {
  return get<IssueTypesSummary>(
    IssuesV2Endpoints.TYPES_SUMMARY,
    true,
    { scope },
    { encodeQuery: true },
  );
}

/**
 * Query key for the issue types summary query.
 * Uses the scope to uniquely identify the query.
 */
export const getIssueTypesSummaryQueryKey = (scope: Scope): QueryKey => [
  'issues',
  'types',
  'summary',
  scope,
];

/**
 * Hook for fetching issue types summary for a given scope.
 *
 * The scope determines what issues are counted:
 * - GlobalScope: All issues across the organization
 * - ProjectScope: Issues for a specific project/revision (optionally filtered by revisionScanId)
 * - ReleaseGroupScope: Issues for a specific release group
 *
 * @param scope - The scope to fetch issue types for
 * @param shouldFetch - Whether to fetch the query (default: true)
 *
 * @example
 * ```typescript
 * // Fetch for a specific revision scan
 * const scope: ProjectScope = {
 *   type: IssueScopeKind.Project,
 *   id: 'git+github.com/owner/repo',
 *   revision: 'abc123',
 *   revisionScanId: 456,
 * };
 * const { data: issueTypesSummary } = useIssueTypesSummary(scope);
 * ```
 */
export function useIssueTypesSummary(scope: Scope, shouldFetch = true) {
  return useQuery<IssueTypesSummary>({
    queryKey: getIssueTypesSummaryQueryKey(scope),
    queryFn: () => fetchIssueTypesSummary(scope),
    enabled: shouldFetch,
  });
}
